/**
 * lp-pricing.js — pure data layer for the LP virtual-cart pricing/discount logic.
 *
 * NO DOM, NO globals, NO side effects: every external dependency (notably the Hy
 * discount calculator) is INJECTED as a plain function/object. That keeps this file
 * trivially loadable in Node, so the decision logic can be unit-tested with a fake
 * calculator — no browser, no dev server, no jsdom. The custom elements in
 * landing-page.js are thin adapters: read the DOM → call these → write the DOM.
 *
 * Dual-exposed (UMD-ish): `window.LpPricing` in the browser, `module.exports` in Node.
 *
 * Units: `formatMoney` takes CENTS. Everything else works in MAJOR currency units
 * (euros) — the same units as the `variant-price` attribute and the Hy calculator.
 */
(function (root, factory) {
  const api = factory();
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  if (root) root.LpPricing = api;
})(typeof window !== "undefined" ? window : null, function () {
  // Shopify's standard money formatter. Input is in CENTS.
  function formatMoney(cents, format) {
    const formatWithDelimiters = function (
      number,
      precision,
      thousands,
      decimal,
    ) {
      precision = typeof precision != "undefined" ? precision : 2;
      thousands = typeof thousands != "undefined" ? thousands : ".";
      decimal = typeof decimal != "undefined" ? decimal : ",";
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split(".");
      let dollars = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        "$1" + thousands,
      );
      let cents = parts[1] ? decimal + parts[1] : "";
      return dollars + cents;
    };
    let placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    let money_format = format;
    let value = null;
    switch (money_format.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2, ",", ".");
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
    }
    return money_format.replace(placeholderRegex, value);
  }

  // Number of SELECTED shade units in a cart (the milestone/threshold count). Auto-added
  // gifts (type 'upsell') don't count toward thresholds — only 'default' lines do.
  function selectionCount(lines) {
    return (lines || [])
      .filter((l) => l.type === "default")
      .reduce((acc, l) => acc + l.quantity, 0);
  }

  // A gift/upsell is "earned" (free, locked in) once the selected count meets its trigger.
  function isEarned(count, triggerQuantity) {
    return triggerQuantity > 0 && count >= triggerQuantity;
  }

  // Original/discounted whole-cart totals (CENTS). `api` is the injected Hy calculator
  // ({ calculateTotal, calculateCompareAtTotal, calculateDiscountedTotal }) or falsy → sum
  // the line prices as a fallback (no live discount).
  function cartTotals(cart, basePrice, api) {
    const sum = (key) =>
      cart.lines.reduce(
        (acc, l) => acc + (Number(l[key]) || Number(l.price) || 0) * l.quantity,
        0,
      );
    if (api) {
      const original =
        basePrice === "compare_at_price"
          ? api.calculateCompareAtTotal(cart)
          : api.calculateTotal(cart);
      return {
        original: original * 100,
        discounted: api.calculateDiscountedTotal(cart) * 100,
      };
    }
    const original =
      basePrice === "compare_at_price" ? sum("compareAtPrice") : sum("price");
    return { original: original * 100, discounted: sum("price") * 100 };
  }

  // Effective per-unit price (MAJOR units) of one line under the live cart. The Hy app
  // discounts cart lines but exposes only whole-cart totals, so derive the line's price by
  // diffing the discounted total WITH vs WITHOUT it — no rule is hardcoded, so any config
  // ("2nd add-on 50% off at N shades", etc.) is honoured as data. When the line isn't in
  // the cart, the diff simulates adding one unit (so a tile can advertise the discount
  // before it's selected). Returns `full` when there's no calculator, no line, or no real
  // saving (delta ≥ full, e.g. earned/free — handled elsewhere — or a phantom).
  //   cart           — base cart object (lines + currency/localization)
  //   line           — the line's data ({ variantId, productId, price, ... })
  //   full           — its full unit price (major units)
  //   calcDiscounted — injected (cart) => number, the Hy calculateDiscountedTotal
  function lineDiscountedPrice({ cart, line, full, calcDiscounted }) {
    if (typeof calcDiscounted !== "function" || !line) return full;
    const variantId = line.variantId;
    const without = cart.lines.filter((l) => l.variantId !== variantId);
    const withLine = without.concat([{ ...line, quantity: 1 }]);
    try {
      const dWith = calcDiscounted({ ...cart, lines: withLine });
      const dWithout = calcDiscounted({ ...cart, lines: without });
      const delta = dWith - dWithout;
      return delta > 0 && delta < full ? delta : full;
    } catch (e) {
      return full;
    }
  }

  // Derive the ordered milestone ladder for a page's products from the parsed Hy config:
  // scalePrice quantity tiers (type 'discount'), 100%-off gift tiers (type 'gift'), and a
  // synthetic base node at qty 1 (type 'base', lowest unit price). Pure — the caller
  // supplies the page's product ids and unit prices (read from the DOM) and the currency.
  function parseDiscountConfig(cfg, pageProductIds, prices, currency) {
    const pageProducts =
      pageProductIds instanceof Set
        ? pageProductIds
        : new Set((pageProductIds || []).map(String));
    if (!pageProducts.size) return [];
    const cur = currency || "EUR";
    const amountFor = (action) =>
      action.amountByCurrency && action.amountByCurrency[cur] != null
        ? action.amountByCurrency[cur]
        : action.amount;

    const byQty = new Map();
    const at = (q) => {
      let m = byQty.get(q);
      if (!m) {
        m = { quantity: q };
        byQty.set(q, m);
      }
      return m;
    };

    (cfg.scalePrices || []).forEach((group) => {
      if (!(group.products || []).some((p) => pageProducts.has(String(p))))
        return;
      (group.stages || []).forEach((stage) => {
        const q = stage.requirement && stage.requirement.quantity;
        if (!q) return;
        const m = at(q);
        const amt = amountFor(stage.action);
        if (amt != null && (m.amount == null || amt > m.amount)) m.amount = amt;
        if (!m.type) m.type = "discount";
      });
    });

    (cfg.buyXgetYs || []).forEach((group) => {
      if (!(group.productsX || []).some((p) => pageProducts.has(String(p))))
        return;
      if (!group.action || group.action.percentage !== 100) return; // free gifts only
      const q = group.requirementX && group.requirementX.quantity;
      if (!q) return;
      const m = at(q);
      m.type = "gift";
      m.giftProductIds = (m.giftProductIds || []).concat(group.productsY || []);
    });

    const validPrices = (prices || []).filter((n) => n > 0);
    if (validPrices.length) {
      const m = at(1);
      if (!m.type) {
        m.type = "base";
        m.amount = Math.min.apply(null, validPrices);
      }
    }

    return Array.from(byQty.values()).sort((a, b) => a.quantity - b.quantity);
  }

  // Is `productId` genuinely configured in the Hy discount rules as a scale-priced product, a
  // buy-X trigger product, or a buy-X-get-Y PAID companion (e.g. "buy the eyeliner kit, £6 off
  // this mascara")? The real risk this guards against is a paid line becoming unintentionally
  // FREE — so a productsY match only counts when that rule's action is NOT a 100%-off free gift
  // (action.percentage === 100, e.g. the sharpener/brush-end rules): those Y products are only
  // ever meant to be auto-added complimentary lines, not sold as a normal paid add here.
  function isProductExclusiveEligible(cfg, productId) {
    if (!cfg || productId == null) return false;
    const pid = String(productId);
    const inScale = (cfg.scalePrices || []).some((g) =>
      (g.products || []).some((p) => String(p) === pid),
    );
    const inTrigger = (cfg.buyXgetYs || []).some((g) =>
      (g.productsX || []).some((p) => String(p) === pid),
    );
    const inPaidGift = (cfg.buyXgetYs || []).some(
      (g) =>
        (!g.action || g.action.percentage !== 100) &&
        (g.productsY || []).some((p) => String(p) === pid),
    );
    return inScale || inTrigger || inPaidGift;
  }

  return {
    formatMoney,
    selectionCount,
    isEarned,
    cartTotals,
    lineDiscountedPrice,
    parseDiscountConfig,
    isProductExclusiveEligible,
  };
});
